import React, {useEffect, useMemo, useRef, useState} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {createPortal} from 'react-dom';
import _ from 'lodash';
import { isMobile } from 'react-device-detect'
import {
    closestCenter,
    pointerWithin,
    rectIntersection,
    DndContext,
    DragOverlay,
    defaultDropAnimation,
    getFirstCollision,
    PointerSensor,
    TouchSensor,
    useSensors,
    useSensor,
    MeasuringStrategy,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
	horizontalListSortingStrategy,
    rectSwappingStrategy
} from '@dnd-kit/sortable';
import { v4 as uuidv4 } from 'uuid';

import { Virtuoso } from 'react-virtuoso';
import SortableTreeItem from './SortableTreeItem'
import { Draggable } from 'components'
import { AnimatePresence, motion } from 'framer-motion';

import { getProjection } from './utilities';

const TRASH_ID = 'void';

const Wrapper = styled.div`
    height: 100%;
    display: ${props => props.direction === 'VERTICAL' ? 'flex' : 'grid'};
    flex-direction: column;
    grid-template-columns: ${props => props.direction === 'HORIZONTAL' && '1fr auto'};
    grid-column-gap: 1rem;
    max-width: calc(100vw - 0.5rem);
`

const Container = styled.div`
    height: 100%;
    width: 100%;
    display: ${props => props.direction === 'HORIZONTAL' ? 'grid' : 'flex'};
    flex-direction: column;
    grid-template-columns: ${props => `repeat(${props.n}, ${props.styles.width || 'auto'})`};
    grid-column-gap: ${props => props.styles.gap};
    overflow-x: ${props => props.direction === 'HORIZONTAL' ? 'auto' : 'visible'}; // May cause FaGrip to be hidden for listView when direction === 'VERTICAL'
    padding: ${props => props.styles.padding};
    margin: ${props => props.styles.margin};
    /* display: flex;
	flex-direction: ${props => props.direction === 'HORIZONTAL' ? 'row' : 'column'};
    height: 100%;
    width: ${props => props.direction === 'HORIZONTAL' ? 'auto' : '100%'};
    overflow-x: ${props => props.direction === 'HORIZONTAL' ? 'auto' : 'visible'}; // May cause FaGrip to be hidden for listView when direction === 'VERTICAL' */
`

const TreeContainer = styled.div`
    border: 1px solid red;
`

const CollapseIndicator = styled.div`
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.2rem;
    height: 1.2rem;
    border-radius: 50%;
    background-color: var(--focus-color);
    font-size: 0.8rem;
    font-weight: bolder;
    color: #fff;
`

const Placeholder = styled.div`
    z-index: 2;
    right: 1rem;
    top: 3.5rem;
    position: ${props => props.position};
    height: ${props => props.show ? 'calc(100vh - 2.5rem - 2rem)' : 'auto'};
`

let defaultStyle = {
	margin: '0 0 0.5rem 0',
    treeHeight: '100%',
    treeWidth: '100%',
    border: '1px solid transparent',
}

let dragStyle = {
	...defaultStyle,
	border: '1px solid var(--secondary-color)',
	padding: '1rem',
	opacity: '0.5',
	borderRadius: '5px',
    maxHeight: '30vh',
    overflow: 'hidden',
    // minWidth: '22rem'
}

const defaultContainerStyle = {

}

const SortableForest = props => {
    const {
        id,
        trees,
		dragOverlay,
        onStart,
		onDrop,
		direction,
		disableNesting,
        disableDND,
		styles,
        containerStyles,
        treeStyles,
        dragStyles,
        disableVirtual,
        disableVirtualWindowScroll,
        indentationWidth = 24,
        dragOverlayWidth = '10rem',
        defaultParents = [],
        secondaryContainer,
        showSecondary,
        afterForest,
        autoScroll=true,
        endReached
    } = props
    const start_time = performance.now()
    const debug = false
    const resetOnEnd = false
	defaultStyle = {...defaultStyle, ...styles}
	dragStyle = {...dragStyle, ...dragStyles}
    const containerStyle = {...defaultContainerStyle, ...containerStyles}

    const containerRef = React.useRef()
    const [initialPosition, setInitialPosition] = React.useState({ scrollY: 0, scrollX: 0 })
    const body = useSelector(state => state.content.body)
    const clientWidth = body && body.clientWidth
    const [sessionId, setSessionId] = useState(uuidv4())
    const [items, setItems] = useState(() => {
        let output = {}
        trees.map(tree => {
            output[tree['id']] = tree['tree'].map(treeItem => treeItem['id'])
        })
        return output
    })

    const itemsRef = React.useRef()

    const [flattenedItems, setFlattenedItems] = useState(() => {
        let output = {}
        trees.map(tree => {
            tree['tree'].forEach(treeItem => {
				output[treeItem['id']] = treeItem
            })
        })
        return output
    })

	useEffect(() => {
		let updatedItems = {}
        let updatedFlattened = {}
        trees.map(tree => {
            updatedItems[tree['id']] = tree['tree'].map(treeItem => treeItem['id'])
            tree['tree'].forEach(treeItem => {
				updatedFlattened[treeItem['id']] = treeItem
            })
        })
        setItems(updatedItems)
        setFlattenedItems(updatedFlattened)

        // Update containers
        const updatedContainers = trees.map(tree => tree['id'])
        !_.isEqual(updatedContainers, containers) && setContainers((updatedContainers))
        // console.log('[update items]', {trees, updatedItems, updatedFlattened})
        // console.log(`[SortableForest] Build vieww ${((performance.now() - start_time)/1000).toFixed(2)}s`)
	}, [trees])

    useEffect(() => {
        requestAnimationFrame(() => {
            recentlyMovedToNewContainer.current = false;
        });
    }, [items]);

    // DND state
    const [activeId, setActiveId] = useState(null);
    const lastOverId = useRef(null);
    const recentlyMovedToNewContainer = useRef(false);
    const [clonedItems, setClonedItems] = useState(null);
    const [containers, setContainers] = useState(trees.map(tree => tree['id']))
	const [collapseCount] = useMemo(() => {
        // Containers
        // const containersMemo = trees.map(tree => tree['id'])
        // setContainers(containersMemo)

        // Collapse count
        let collapseMemo = 0
        if(containers.includes(activeId)){
            collapseMemo = trees.find(tree => tree['id'] === activeId)['tree'].length
        } else {
            collapseMemo = flattenedItems ? Object.values(flattenedItems).filter(item => item['parents'].includes(activeId ? activeId.replace('_ItemModal', '') : activeId)).length : 0
        }

		return [collapseMemo]
	}, [activeId, items, trees])

    // Position state
    const [offsetLeft, setOffsetLeft] = React.useState(0);
    debug && console.log('[SortableForest]', {id, trees, flattenedItems, collapseCount})

    const collisionDetectionStrategy = React.useCallback(
        (args) => {
            if (activeId && activeId in items) {
                return closestCenter({
                    ...args,
                    droppableContainers: args.droppableContainers.filter(
                        (container) => container.id in items
                    ),
                });
            }
        
            // Start by finding any intersecting droppable
            const pointerIntersections = pointerWithin(args);
            const intersections =
                pointerIntersections.length > 0
                ? // If there are droppables intersecting with the pointer, return those
                    pointerIntersections
                : rectIntersection(args);
            let overId = getFirstCollision(intersections, 'id');
        
            if (overId != null) {
                if (overId === TRASH_ID) {
                    // If the intersecting droppable is the trash, return early
                    // Remove this if you're not using trashable functionality in your app
                    return intersections;
                }
        
                if (overId in items) {
                    const containerItems = items[overId];
        
                    // If a container is matched and it contains items (columns 'A', 'B', 'C')
                    if (containerItems.length > 0) {
                        // Return the closest droppable within that container
                        overId = closestCenter({
                        ...args,
                        droppableContainers: args.droppableContainers.filter(
                            (container) =>
                            container.id !== overId &&
                            containerItems.includes(container.id)
                        ),
                        })[0]?.id;
                    }
                }
        
                lastOverId.current = overId;
        
                return [{id: overId}];
            }
        
            // When a draggable item moves to a new container, the layout may shift
            // and the `overId` may become `null`. We manually set the cached `lastOverId`
            // to the id of the draggable item that was moved to the new container, otherwise
            // the previous `overId` will be returned which can cause items to incorrectly shift positions
            if (recentlyMovedToNewContainer.current) {
                lastOverId.current = activeId;
            }
        
            // If no droppable is matched, return the last match
            return lastOverId.current ? [{id: lastOverId.current}] : [];
        },
        [activeId, items]
    );
    // const projected = {}
	const projected = !disableNesting && activeId && !containers.includes(activeId) && lastOverId && lastOverId.current 
					&& getProjection(flattenedItems, items, activeId,lastOverId.current,offsetLeft,indentationWidth,collapseCount)
    // projected && console.log(projected.depth, offsetLeft)

    // Utilities
    const findContainer = (id) => {
        if (id in items) {
            return id;
        }
        return Object.keys(items).find((key) => items[key].includes(id));
    }

    // DND handlers
    const onDragStart = ({active}) => {
        setActiveId(active.id);
        setClonedItems(items);
        onStart && onStart(active)
        containerRef && containerRef.current && setInitialPosition({
            scrollY: containerRef.current.scrollTop,
            scrollX: containerRef.current.scrollLeft
        })

		document.body.style.setProperty('cursor', 'grabbing');
    }

    const onDragOver = ({active, over}) => {
        const overId = over?.id;
        if (!overId || overId === TRASH_ID || active.id in items) { return }

        const overContainer = findContainer(overId);
        const activeContainer = findContainer(active.id);
        if (!overContainer || !activeContainer) { return }

        if (activeContainer !== overContainer) {
            const tree = trees.find(tree => tree['id'] === overContainer)
            if(!tree['visibility']) { return }
            console.log('[DRAG_OVER]', {activeId: active.id, overId: overId, overContainer, activeContainer, trees})

            setItems((items) => {
                const activeItems = items[activeContainer];
                const overItems = items[overContainer];
                const overIndex = overItems.indexOf(overId);
                const activeIndex = activeItems.indexOf(active.id);

                let newIndex;

                if (overId in items) {
                    newIndex = overItems.length + 1;
                } else {
                    const isBelowOverItem = over && active.rect.current.translated &&
                                            active.rect.current.translated.top > over.rect.top + over.rect.height;
                    const modifier = isBelowOverItem ? 1 : 0;
                    newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
                }

                recentlyMovedToNewContainer.current = true;
                const updatedItems = {
                    ...items,
                    // [activeContainer]: items[activeContainer].filter((item) => item !== active.id),
                    [activeContainer]: items[activeContainer].filter((item) => item !== active.id),
                    [overContainer]: [
                        ...items[overContainer].slice(0, newIndex),
                        items[activeContainer][activeIndex],
                        ...items[overContainer].slice(
                        newIndex,
                        items[overContainer].length
                        ),
                    ],
                }

                // let updatedFlattenedItems = {}
                // Object.keys(updatedItems).forEach(containerId => {
                //     Object.values(updatedItems[containerId]).forEach(itemId => {
                //         updatedFlattenedItems[itemId] = flattenedItems[itemId]
                //     })
                // })
                // setFlattenedItems(updatedFlattenedItems)
                return updatedItems;
            });
        }
    }

    const _onDragOver = ({active, over}) => {
        const overId = over?.id;
        if (!overId || overId === TRASH_ID || active.id in itemsRef.current) { return }

        const overContainer = findContainer(overId);
        const activeContainer = findContainer(active.id);
        if (!overContainer || !activeContainer) { return }

        if (activeContainer !== overContainer) {
            const tree = trees.find(tree => tree['id'] === overContainer)
            if(!tree['visibility'] || showSecondary) { return }
            console.log('[DRAG_OVER]', {activeId: active.id, overId: overId, overContainer, activeContainer, trees})

            const activeItems = itemsRef.current[activeContainer];
            const overItems = itemsRef.current[overContainer];
            const overIndex = overItems.indexOf(overId);
            const activeIndex = activeItems.indexOf(active.id);

            let newIndex;

            if (overId in itemsRef.current) {
                newIndex = overItems.length + 1;
            } else {
                const isBelowOverItem = over && active.rect.current.translated &&
                                        active.rect.current.translated.top > over.rect.top + over.rect.height;
                const modifier = isBelowOverItem ? 1 : 0;
                newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }

            recentlyMovedToNewContainer.current = true;
            const updatedItems = {
                ...itemsRef.current,
                // [activeContainer]: items[activeContainer].filter((item) => item !== active.id),
                [activeContainer]: itemsRef.current[activeContainer].filter((item) => item !== active.id),
                [overContainer]: [
                    ...itemsRef.current[overContainer].slice(0, newIndex),
                    itemsRef.current[activeContainer][activeIndex],
                    ...itemsRef.current[overContainer].slice(
                    newIndex,
                    itemsRef.current[overContainer].length
                    ),
                ],
            }
            console.log(updatedItems['6314483658ccab8f640a8acb'])
            itemsRef.current = updatedItems
        }
    }

    const onDragEnd = (values) => {
        const activeId = values.active['id']
        const overId = values.over['id']
        const dndType = containers.includes(activeId) ? 'CONTAINER' 
                        : ['CALENDAR'].includes(overId.split('_')[0]) ? 'ACCESS'
                        : 'ITEM'

        if(['ITEM', 'CONTAINER'].includes(dndType)){
            // const oldItem = _.cloneDeep(flattenedItems[activeId])
            // const oldContainers = _.cloneDeep(containers)
            let oldItem = _.cloneDeep(_.omit(flattenedItems[activeId], 'block'))
            const oldContainers = JSON.parse(JSON.stringify(containers))

            let activeIndex
            let overIndex
            switch(dndType){
                case 'CONTAINER':
                    activeIndex = containers.indexOf(activeId)
                    overIndex = containers.indexOf(overId)
                    let updatedContainers = [...containers]
                    const isBelow = activeIndex > overIndex
                    updatedContainers.splice(activeIndex, 1)
                    overIndex = isBelow ? overIndex : updatedContainers.indexOf(overId) + 1
                    updatedContainers.splice(overIndex,0,activeId)
                    setContainers(updatedContainers)
                    console.log('[DRAG_END]', {activeIndex, overIndex, updatedContainers})
                    onDrop && onDrop({
                        type: 'CONTAINER',
                        oldContainers,
                        containers: updatedContainers
                    })

                    resetDND()
                    resetOnEnd && setSessionId(uuidv4())
                    break
                case 'ITEM':
                    // [NOTE] Active item should take from old (i.e. clonedItems), over item should take from updated (i.e. items)
                    const activeItem = flattenedItems[values.active['id']]
                    const overItem = flattenedItems[values.over['id']]
                    const activeContainer = activeItem['containerId']
                    const overContainer = findContainer(values.over['id'])
                    let depth = values && values['depth'] ? values['depth'] + defaultParents.length
                                : projected && projected.depth ? projected.depth + defaultParents.length
                                : 0

                    let updatedItems = {...clonedItems}
                    activeIndex = clonedItems[activeContainer].indexOf(activeItem['id'])
                    overIndex = items[overContainer].indexOf(overItem['id']) 
                    const isBelowOverItem = activeContainer === overContainer ? activeIndex > overIndex : containers.indexOf(activeContainer) > containers.indexOf(overContainer)
                    false && console.log('[BEFORE DRAG_END]',{
                        activeIndex, overIndex, 
                        activeId: activeItem['id'],
                        overId: overItem['id'],
                        activeContainer: activeContainer,
                        overContainer: overContainer,
                        collapseCount, 
                        depth,
                        activeDepth: activeItem['depth']
                    })
                    if(values.active['id'] === values.over['id'] && activeContainer === overContainer && depth === activeItem['depth']) { 
                        console.log('[INTERRUPT]')
                        resetDND()
                        resetOnEnd && setSessionId(uuidv4())
                        return
                    }

                    // Remove from original position then splice into over position
                    const shouldSplice = ((activeId !== overId) || (activeContainer !== overContainer))
                    const insertIds = shouldSplice ? updatedItems[activeContainer].splice(activeIndex, collapseCount + 1) : updatedItems[activeContainer].slice(activeIndex, activeIndex + collapseCount + 1)
                    if(activeContainer === overContainer && values.active['id'] !== values.over['id']){
                        overIndex = !isBelowOverItem ? overIndex - collapseCount : updatedItems[overContainer].indexOf(overItem['id'])
                    }
                    shouldSplice && updatedItems[overContainer].splice(overIndex, 0, ...insertIds)
                    setItems(updatedItems)

                    // Update flattenedItems with the new depth and container if changed
                    let updatedFlattenedItems = {...flattenedItems}
                    const depthIncrement = depth - activeItem['depth']
                    const itemBefore = updatedFlattenedItems[updatedItems[overContainer][overIndex - 1]]
                    let children = []
                    insertIds.forEach((itemId, index) => {
                        if(itemId === activeId){
                            // console.log({depth, depthIncrement, itemBefore})
                            // Update active item
                            updatedFlattenedItems[itemId]['depth'] = depth
                            updatedFlattenedItems[itemId]['containerId'] = overContainer
                            updatedFlattenedItems[itemId]['parents'] = !itemBefore ? defaultParents
                                                                    : depth === 0 ? defaultParents
                                                                    // : depthIncrement === 0 ? updatedFlattenedItems[itemId]['parents']
                                                                    : depthIncrement === 0 ? [...itemBefore['parents'], itemBefore['id']] 
                                                                    : depthIncrement > 0 & itemBefore['parents'].length === depth ? [...itemBefore['parents']] 
                                                                    : depthIncrement > 0 & itemBefore['parents'].length < depth ? [...itemBefore['parents'], itemBefore['id']] 
                                                                    : depthIncrement > 0 & itemBefore['parents'].length > depth ? itemBefore['parents'].slice(0,depthIncrement)
                                                                    : depthIncrement < 0 & itemBefore['parents'].length === depth ? [...itemBefore['parents']]
                                                                    : depthIncrement < 0 & itemBefore['parents'].length > depth ? itemBefore['parents'].slice(0,depth)
                                                                    : depthIncrement < 0 & itemBefore['parents'].length > 0 ? itemBefore['parents'].slice(0,depthIncrement)
                                                                    : depthIncrement < 0 & itemBefore['parents'].length === 0 ? [itemBefore['id']]
                                                                    : defaultParents
                            updatedFlattenedItems[itemId]['parentId'] = updatedFlattenedItems[itemId]['parents'].slice(-1)[0]
                            console.log({parents: updatedFlattenedItems[itemId]['parents'], itemBefore, depthIncrement, depth, values})
                            
                        } else {
                            // Update children info
                            const parentId = updatedFlattenedItems[itemId]['parentId']
                            updatedFlattenedItems[itemId]['depth'] += depthIncrement
                            updatedFlattenedItems[itemId]['containerId'] = overContainer
                            updatedFlattenedItems[itemId]['parents'] = [...updatedFlattenedItems[parentId]['parents'], parentId]
                            children.push(updatedFlattenedItems[itemId])
                        }
                    })
                    
                    setFlattenedItems(updatedFlattenedItems)
                    debug && console.log('[DRAG_END]',{
                        itemBefore,
                        depth, depthIncrement,
                        activeIndex, overIndex, 
                        activeId: activeItem['id'],
                        overId: overItem['id'],
                        activeContainer: activeContainer,
                        overContainer: overContainer,
                        insertIds, collapseCount, 
                        items: updatedItems,
                        flattenedItems: updatedFlattenedItems
                    })

                    onDrop && onDrop({
                        type: 'ITEM',
                        oldItem: oldItem,
                        item: activeItem,
                        children,
                        items: updatedItems
                    })
                    resetDND()
                    resetOnEnd && setSessionId(uuidv4())
                    break
                default:
                    resetDND()
                    resetOnEnd && setSessionId(uuidv4())
            }
        } else if (dndType === 'ACCESS') {
            const module = overId.split('_')[0]
            const schedule = values.over.data.current

            switch(module){
                case 'CALENDAR':
                    onDrop && onDrop({ type: 'CALENDAR', activeId, schedule })
                    resetDND(lastOverId.current.includes('CALENDAR'))
                    resetOnEnd && setSessionId(uuidv4())
                    break
            }
        }

        
    }

    const onDragCancel = () => {
        console.log('[DRAG_CANCEL]')
        resetDND()
    };

    const onDragMove = ({ delta }) => {
        // direction !== 'HORIZONTAL' && setOffsetLeft(delta.x);
        if(disableNesting) { return }
        let newDepth = Math.round(delta.x / indentationWidth)
        const oldDepth = offsetLeft ? Math.round(offsetLeft / indentationWidth) : 0
        if(projected) {
            const { maxDepth, minDepth } = projected
            newDepth = newDepth > maxDepth ? maxDepth
                        : newDepth < minDepth ? minDepth
                        : newDepth
            newDepth !== oldDepth && console.log('[onDragMove]',{newDepth, oldDepth, projected: projected && projected.depth, maxDepth, minDepth})
        }
        newDepth !== oldDepth && setOffsetLeft(newDepth * indentationWidth)
    }

    const resetDND = (returnToInitial) => {
        setActiveId(null);
        setClonedItems(null);
        setOffsetLeft(null)
        lastOverId.current = null
		document.body.style.setProperty('cursor', '');
        returnToInitial && returnInitial()
    }

	const renderOverlay = (id) => {
		const type = containers.includes(id) ? 'container' : 'item'
		if(dragOverlay){
			const collapseIndicator = collapseCount > 0 ? <CollapseIndicator>{collapseCount}</CollapseIndicator> : null
			return (
                <div style={{
                    zIndex: 2301,
                    opacity: 1,
                    // opacity: type === 'item' ? 0 : 1,
                    // width: type === 'container' ? dragOverlayWidth : '100%', 
                    width: '100%',
                    position: type === 'container' ? 'absolute' : 'absolute',
                    transform: `translateY(${window.scrollY}px)`
                    }}>
                    {dragOverlay(id)[type]}
                    {collapseIndicator}
                </div>
            )
            
		} else {
			return type === 'item' ? flattenedItems[id]['block'] : trees.find(tree => tree['id'] === id)['block']
		}
		
	}

    const returnInitial = () => {
        // console.log('GO BACK INITIAL')
        containerRef.current.scrollTo({
            left: initialPosition['scrollX'], 
            top: initialPosition['scrollY'],
            behavior: 'smooth'
        })
        setInitialPosition({ scrollY: 0, scrollX: 0 })
    }
    
    const getSecContainer = () => {
        if(!showSecondary) { return }
        if(clientWidth < 1000) {
            return <motion.div style={{marginBottom: '2rem'}}
                transition={{ duration: 0.3 }} initial="closed" animate="open" exit="closed"
                variants={{
                    open: { height: 'auto'},
                    closed: { height: 0 }
                }}>
                {secondaryContainer}
            </motion.div>
        } else { 
            return <motion.div key='asd'
                transition={{ duration: 0.3 }} initial="closed" animate="open" exit="closed"
                variants={{
                    open: { translateX: 0, originX: 0 },
                    closed: { translateX: 500, originX: 0 }
                }}>
                {secondaryContainer}
            </motion.div>
        }
    }

    const getContainer = (containerId) => {
        if(!Object.keys(items).includes(containerId)){ return }
        const tree = trees.find(tree => tree['id'] === containerId)
        if(!tree) { return }

        let content
        if(!disableVirtual && items[containerId].length > 0) {
            content = <Virtuoso 
                        key={`${containerId}_Virtuoso`}
                        style={{ 
                            height: '100%', overflowX: 'hidden', 
                            overflowY: !disableVirtual && !disableVirtualWindowScroll && 'hidden' // to prevent scrollbar within each section in ListView
                        }}
                        useWindowScroll={!disableVirtualWindowScroll}
                        defaultItemHeight={1}
                        totalCount={!disableVirtual && direction === 'HORIZONTAL' ? items[containerId].length + 1 : items[containerId].length}
                        itemContent={index => {
                            if(index === items[containerId].length && direction === 'HORIZONTAL' && tree['afterTree'] ){ return tree['afterTree'] }
                            const treeItemId = items[containerId][index]
                            const treeItem = flattenedItems[treeItemId]
                            // treeItemId === activeId && console.log({projected: projected && projected.depth, tree: treeItem['depth']})
                            if(treeItem && !treeItem['parents'].includes(activeId ? activeId.replace('_ItemModal','') : activeId)){
                                return (
                                    <SortableTreeItem key={treeItemId} id={treeItemId} 
                                        containerId={containerId} 
                                        disableDrag={treeItem['disableDrag'] || disableDND}
                                        depth={treeItemId === activeId && projected && !disableNesting ? projected.depth : treeItem['depth']}>
                                        {treeItem['block']}
                                    </SortableTreeItem>
                                )
                            } else {
                                return <div/>
                            }
                        }}/>
        } else if (direction === 'HORIZONTAL' && items[containerId].length === 0 && !disableVirtual) {
            content = tree['afterTree'] || <div/>
        } else {
            content = items[containerId].map(treeItemId => {
                const treeItem = flattenedItems[treeItemId]
                if(treeItem && !treeItem['parents'].includes(activeId ? activeId.replace('_ItemModal','') : activeId)){
                    // treeItemId === activeId && console.log({projected: projected && projected.depth, tree: treeItem['depth']})
                    return (
                        <SortableTreeItem key={treeItemId} id={treeItemId} type='ITEM'
                            collapseCount={collapseCount > 0 && activeId === treeItemId && collapseCount}
                            containerId={containerId} 
                            disableDrag={treeItem['disableDrag'] || disableDND}
                            depth={treeItemId === activeId && projected && !disableNesting ? projected.depth : treeItem['depth']}>
                            {treeItem['block']}
                        </SortableTreeItem>
                    )
                }
            })
        }

        return (
            <div key={containerId} style={{height: defaultStyle.treeHeight, width: defaultStyle.treeWidth}}>
                <SortableTreeItem 
                    key={containerId} 
                    id={containerId} 
                    value={containerId} 
                    type='CONTAINER'
                    disableVirtual={disableVirtual} 
                    disableDrag={tree['disableDrag'] || disableDND} 
                    styles={tree['disableDrag'] ? defaultStyle : activeId === containerId ? dragStyle : defaultStyle}
                    >
                    {tree['beforeTree']}
                    <SortableContext disabled={disableDND ? true : false}
                        items={items[containerId].filter(itemId => itemId && !flattenedItems[itemId]['parents'].includes(itemId))} 
                        strategy={verticalListSortingStrategy} 
                        id={containerId} 
                        key={`${sessionId}_${containerId}`} // to force items to be updated
                        >
                        <div style={treeStyles}>{content}</div>
                    </SortableContext>
                    {!tree['afterTree'] ? null : direction !== 'HORIZONTAL' | disableVirtual && <div>{tree['afterTree']}</div>}
                </SortableTreeItem>
            </div>
        )
    }

    const _getContainer = (containerId) => {
        if(!itemsRef.current) { return }
        if(!Object.keys(itemsRef.current).includes(containerId)){ return }
        const tree = trees.find(tree => tree['id'] === containerId)
        if(!tree) { return }

        let content
        if(!disableVirtual && itemsRef.current[containerId].length > 0) {
            content = <Virtuoso key={`${containerId}_Virtuoso`} id='virtuo'
                        style={{ 
                            height: '100%', overflowX: 'hidden', 
                            overflowY: !disableVirtual && !disableVirtualWindowScroll && 'hidden' // to prevent scrollbar within each section in ListView
                        }}
                        useWindowScroll={!disableVirtualWindowScroll}
                        defaultItemHeight={1}
                        totalCount={!disableVirtual && direction === 'HORIZONTAL' ? itemsRef.current[containerId].length + 1 : itemsRef.current[containerId].length}
                        itemContent={index => {
                            if(index === itemsRef.current[containerId].length && direction === 'HORIZONTAL' && tree['afterTree'] ){ return tree['afterTree'] }
                            const treeItemId = itemsRef.current[containerId][index]
                            const treeItem = flattenedItems[treeItemId]
                            if(treeItem){
                                return (
                                    <SortableTreeItem key={treeItemId} id={treeItemId} 
                                        containerId={containerId} 
                                        disableDrag={treeItem['disableDrag'] || disableDND}
                                        depth={treeItemId === activeId && projected && !disableNesting ? projected.depth : treeItem['depth']}>
                                        {treeItem['block']}
                                    </SortableTreeItem>
                                )
                            }
                        }}/>
        } else if (direction === 'HORIZONTAL' && itemsRef.current[containerId].length === 0 && !disableVirtual) {
            content = tree['afterTree'] || <div/>
        } else {
            content = itemsRef.current[containerId].map(treeItemId => {
                const treeItem = flattenedItems[treeItemId]
                if(treeItem && !treeItem['parents'].includes(activeId ? activeId.replace('_ItemModal','') : activeId)){
                    return (
                        <SortableTreeItem key={treeItemId} id={treeItemId} type='ITEM'
                            collapseCount={collapseCount > 0 && activeId === treeItemId && collapseCount}
                            containerId={containerId} 
                            disableDrag={treeItem['disableDrag'] || disableDND}
                            depth={treeItemId === activeId && projected && !disableNesting ? projected.depth : treeItem['depth']}>
                            {treeItem['block']}
                        </SortableTreeItem>
                    )
                }
            })
        }

        return (
            <div key={containerId} style={{height: defaultStyle.treeHeight, width: defaultStyle.treeWidth}}>
                <SortableTreeItem 
                    key={containerId} 
                    id={containerId} 
                    value={containerId} 
                    type='CONTAINER'
                    disableVirtual={disableVirtual} 
                    disableDrag={tree['disableDrag'] || disableDND} 
                    styles={tree['disableDrag'] ? defaultStyle : activeId === containerId ? dragStyle : defaultStyle}
                    >
                    {tree['beforeTree']}
                    <SortableContext disabled={disableDND ? true : false}
                        items={itemsRef.current[containerId].filter(itemId => itemId && !flattenedItems[itemId]['parents'].includes(itemId))} 
                        strategy={verticalListSortingStrategy} 
                        id={containerId} 
                        key={`${sessionId}_${containerId}`} // to force items to be updated
                        >
                        <div style={treeStyles}>{content}</div>
                    </SortableContext>
                    {direction !== 'HORIZONTAL' && <div>{tree['afterTree']}</div>}
                </SortableTreeItem>
            </div>
        )
    }

    const touchSensor = useSensor(TouchSensor, {activationConstraint: {delay: 250, tolerance: 5}})
    const pointSensor = useSensor(PointerSensor, {activationConstraint: { distance: 8 }}) 
    const sensors = isMobile ? touchSensor : pointSensor
    return (
        <DndContext
            autoScroll={clientWidth < 1000 ? true : autoScroll}
            collisionDetection={collisionDetectionStrategy}
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            onDragStart={onDragStart}
            onDragMove={onDragMove}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
            sensors={useSensors(sensors)}>
            <Wrapper direction={clientWidth < 1000 ? 'VERTICAL' : 'HORIZONTAL'}>

                {/* Secondary container */}
                <Placeholder 
                    position={clientWidth < 1000 ? 'auto' : 'fixed'}
                    show={showSecondary}>
                    <AnimatePresence>
                        {getSecContainer()}
                    </AnimatePresence>
                </Placeholder>
            
                <Container 
                    direction={direction} n={afterForest ? containers.length + 1 : containers.length} 
                    styles={containerStyle} 
                    ref={containerRef}
                    >

                    {/* Main container */}
                    <SortableContext 
                        id={sessionId} 
                        items={containers} 
                        // strategy={direction === 'HORIZONTAL' ? horizontalListSortingStrategy : verticalListSortingStrategy}
                        strategy={rectSwappingStrategy}
                        disabled={disableDND ? true : false}
                        >
                        {containers.map(containerId => getContainer(containerId))}
                        {afterForest}
                    </SortableContext>
                    
                    {/* Drag overlay */}
                    {createPortal(
                        <DragOverlay 
                            adjustScale={false} 
                            dropAnimation={{...defaultDropAnimation, dragSourceOpacity: 0.5,}}
                            style={{position: 'absolute', zIndex: 2400}}
                            >
                            {activeId && renderOverlay(activeId)}
                        </DragOverlay>,
                        document.body
                    )}
                </Container>
            </Wrapper>
        </DndContext>
    )
}

export default SortableForest;
